// SCR-017 view model (FLOW-009, API-037). Pure: the filter form and its query, row shaping for
// the table, a deterministic pretty-printer for the content-free detail drawer, and which
// action-domain filters are worth offering a principal (the server is the real gate; this only
// avoids offering a filter that would always answer empty for them).
import type { components } from "../../api/schema";
import type { Rule } from "@nexus/ui";
import { validate, type FieldErrors } from "@nexus/ui";
import { actorDisplay } from "../../ui/identity";
import { addMonths, type MonthRange } from "@nexus/ui";
import { UNKNOWN_TARGET, UNKNOWN_TEAM, UNKNOWN_USER } from "../names";

export type AuditEvent = components["schemas"]["AuditEvent"];
export type AuditEventList = components["schemas"]["AuditEventList"];
export type AuditActorType = components["schemas"]["AuditActorType"];
export type PrincipalRole = components["schemas"]["PrincipalRole"];
export type Permission = components["schemas"]["Permission"];

export const AUDIT_PATH = "/audit";

export interface AuditFilters {
  from: string;
  to: string;
  orgId: string;
  actorId: string;
  action: string;
  targetType: string;
  targetId: string;
}

export const EMPTY_AUDIT_FILTERS: AuditFilters = { from: "", to: "", orgId: "", actorId: "", action: "", targetType: "", targetId: "" };

/** `YYYY-MM-DD` or a full RFC 3339 timestamp (api-contract `from`/`to`); blank passes (server default). */
export const auditDate: Rule = (value) =>
  value.trim() === "" || /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2})?)?$/.test(value.trim())
    ? null
    : "A date as YYYY-MM-DD, or a full timestamp.";

export function validateAuditFilters(filters: AuditFilters): FieldErrors {
  const errors = validate({ from: filters.from, to: filters.to }, { from: [auditDate], to: [auditDate] });
  if (!errors.from && !errors.to && filters.from && filters.to && filters.from > filters.to) {
    errors.to = "Must be on or after the start.";
  }
  return errors;
}

export interface AuditQuery {
  from?: string;
  to?: string;
  orgId?: string;
  actorId?: string;
  action?: string;
  targetType?: string;
  targetId?: string;
  cursor?: string;
}

/** Query object for `GET /v1/admin/audit-events`; blank fields are left for the server's defaults. */
export function auditQuery(filters: AuditFilters, cursor: string | null = null): AuditQuery {
  const query: AuditQuery = {};
  if (filters.from.trim()) query.from = filters.from.trim();
  if (filters.to.trim()) query.to = filters.to.trim();
  if (filters.orgId.trim()) query.orgId = filters.orgId.trim();
  if (filters.actorId.trim()) query.actorId = filters.actorId.trim();
  if (filters.action.trim()) query.action = filters.action.trim();
  if (filters.targetType.trim()) query.targetType = filters.targetType.trim();
  if (filters.targetId.trim()) query.targetId = filters.targetId.trim();
  if (cursor) query.cursor = cursor;
  return query;
}

export interface AuditRowView {
  id: number;
  occurredAt: string;
  actorType: AuditActorType;
  actorId: string;
  /** A user actor's sign-in email when the server knows it (never stored in the trail itself). */
  actorEmail: string | null;
  /** The actor's display name, when the admin directory knows it. */
  actorName: string | null;
  /** For a proxy-admin actor, the display name resolved from the org's user store. */
  actorDisplayName?: string | null;
  /** For a proxy-admin actor, the channel they acted through (e.g., "proxy-admin"). */
  actorVia?: string | null;
  action: string;
  orgId: string | null;
  targetType: string;
  /** The target as it is named now (resolved by the API at read time); null when it has none. */
  targetName: string | null;
  targetId: string;
  requestId: string | null;
  sourceIp: string | null;
}

export function auditRows(events: readonly AuditEvent[]): AuditRowView[] {
  return events.map((event) => ({
    id: event.id,
    occurredAt: event.occurredAt,
    actorType: event.actorType,
    actorId: event.actorId,
    actorEmail: event.actorEmail ?? null,
    actorName: event.actorName ?? null,
    actorDisplayName: event.actorDisplayName ?? null,
    actorVia: event.actorVia ?? null,
    action: event.action,
    orgId: event.orgId,
    targetType: event.targetType,
    targetName: event.targetName,
    targetId: event.targetId,
    requestId: event.requestId,
    sourceIp: event.sourceIp,
  }));
}

/** Who acted, for display: a person's name, else their email, else "You" or "An admin" (never the
 *  subject); a service actor is its credential id, which is what names it. For a proxy-admin,
 *  prefer the display name resolved from the org's user store. */
export function actorLabel(
  row: Pick<AuditRowView, "actorId" | "actorEmail"> & Partial<Pick<AuditRowView, "actorType" | "actorName" | "actorDisplayName">>,
  me?: { subject: string } | null,
): string {
  // For proxy-admin actors, prefer the org-resolved display name
  const displayName = row.actorDisplayName?.trim();
  if (displayName) return displayName;
  const name = row.actorName?.trim();
  if (name) return name;
  if (row.actorEmail) return row.actorEmail;
  return row.actorType === "service" ? row.actorId : actorDisplay(row.actorId, me);
}

/** What a row did, in words, when the action reads better as a sentence and the joined actor names
 *  a person: "<name or email> signed in". Null otherwise, and the action code stands alone. */
export function actionSummary(row: Pick<AuditRowView, "action" | "actorEmail" | "actorName">): string | null {
  if (row.action !== "session.sign_in") return null;
  const who = row.actorName?.trim() || row.actorEmail?.trim();
  return who ? `${who} signed in` : null;
}

const ACTOR_TYPE_LABELS: Record<AuditActorType, string> = { user: "Person", service: "Service", platform: "Platform staff" };

/** The actor's kind in words, under the actor's name. */
export function actorTypeLabel(type: AuditActorType): string {
  return ACTOR_TYPE_LABELS[type];
}

const UNKNOWN_BY_TYPE: Record<string, string> = { user: UNKNOWN_USER, team: UNKNOWN_TEAM };

/** The Target column's words: the target's name, else the type's "Unknown …" wording — never the
 *  id, which a person does not read. */
export function targetText(row: Pick<AuditRowView, "targetType" | "targetName">): string {
  return row.targetName?.trim() || UNKNOWN_BY_TYPE[row.targetType] || UNKNOWN_TARGET;
}

/** The target's reference, `type/id`: the Target cell's hover title and the Target filter's text form. */
export function targetRef(row: Pick<AuditRowView, "targetType" | "targetId">): string {
  return `${row.targetType}/${row.targetId}`;
}

/** The one Target filter box: `type` or `type/id`, as targetRef writes it. */
export function parseTargetFilter(text: string): Pick<AuditFilters, "targetType" | "targetId"> {
  const trimmed = text.trim();
  const slash = trimmed.indexOf("/");
  if (slash < 0) return { targetType: trimmed, targetId: "" };
  return { targetType: trimmed.slice(0, slash).trim(), targetId: trimmed.slice(slash + 1).trim() };
}

/** The API's window for a range of months: from the first day of the first month (inclusive) to
 *  the first day after the last month (the API's `to` is exclusive); null is the whole trail. */
export function monthWindow(range: MonthRange | null): Pick<AuditFilters, "from" | "to"> {
  if (range === null) return { from: "", to: "" };
  return { from: `${range.from}-01`, to: `${addMonths(range.to, 1)}-01` };
}

function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return Object.fromEntries(Object.keys(record).sort().map((key) => [key, sortKeysDeep(record[key])]));
  }
  return value;
}

/** The detail drawer's content: content-free JSON (ENT-034), keys sorted for a stable diff between opens. */
export function detailJson(event: Pick<AuditEvent, "detail">): string {
  return event.detail === null ? "No detail recorded." : JSON.stringify(sortKeysDeep(event.detail), null, 2);
}

/** Action prefixes (api-contract API-037/TASK-129 domain filtering) worth offering as quick filters. */
export function availableActionPrefixes(role: PrincipalRole, permissions: readonly Permission[]): string[] {
  if (role === "super-admin") {
    return ["org", "rate_plan", "assignment", "org_billing_mode", "trial", "adjustment", "period", "expense", "cur", "installer", "aws_account", "session"];
  }
  const prefixes: string[] = [];
  if (permissions.includes("billing:read")) prefixes.push("rate_plan", "assignment", "org_billing_mode", "trial", "adjustment", "period", "expense", "cur");
  if (permissions.includes("installer:run")) prefixes.push("installer", "aws_account");
  return prefixes;
}

/** The actions the admin API records (the server matches `?action=` exactly), by prefix. */
export const KNOWN_ACTIONS: readonly string[] = [
  "org.create",
  "rate_plan.create",
  "rate_plan.update",
  "rate_plan.activate",
  "rate_plan.retire",
  "assignment.create",
  "assignment.delete",
  "org_billing_mode.set",
  "org_billing_mode.cost_visibility.set",
  "trial.create",
  "trial.cancel",
  "adjustment.create",
  "adjustment.approve",
  "adjustment.void",
  "period.close",
  "period.late_unbilled",
  "expense.basis.set",
  "expense.basis.set_default",
  "expense.finalize",
  "expense.restate",
  "cur.source.create",
  "cur.source.verify",
  "cur.source.ingest",
  "cur.source.disable",
  "cur.usage_type.create",
  "cur.usage_type.delete",
  "installer.session.create",
  "installer.session.cancel",
  "installer.session.budget_extend",
  "installer.credentials.supplied",
  "installer.change.approve",
  "installer.change.reject",
  "installer.change.apply",
  "aws_account.link",
  "aws_account.verify",
  "aws_account.unlink",
  "session.sign_in",
  "session.sign_out",
  "session.revoke",
];

const PREFIX_LABELS: Record<string, string> = {
  org: "Customers",
  rate_plan: "Rate plans",
  assignment: "Rate plan assignments",
  org_billing_mode: "Billing mode",
  trial: "Trials",
  adjustment: "Usage adjustments",
  period: "Invoices (month close)",
  expense: "AWS costs",
  cur: "Cost and Usage Reports",
  installer: "Installer",
  aws_account: "Linked AWS accounts",
  session: "Admin sign-in",
};

/** A prefix in words, for the Action filter's groups. */
export function actionPrefixLabel(prefix: string): string {
  return PREFIX_LABELS[prefix] ?? prefix;
}

export interface ActionGroup {
  prefix: string;
  label: string;
  actions: string[];
}

/** The Action filter's choices: the known actions under each prefix the reader may see. A chosen
 *  action that is not in the list (a newer server) still shows, in its own group. */
export function actionGroups(prefixes: readonly string[], chosen = ""): ActionGroup[] {
  const groups = prefixes.map((prefix) => ({
    prefix,
    label: actionPrefixLabel(prefix),
    // `org` must not swallow `org_billing_mode.*`: match the prefix and its dot.
    actions: KNOWN_ACTIONS.filter((action) => action.startsWith(`${prefix}.`)),
  }));
  const trimmed = chosen.trim();
  if (trimmed !== "" && !groups.some((group) => group.actions.includes(trimmed))) {
    groups.push({ prefix: "", label: "Other", actions: [trimmed] });
  }
  return groups.filter((group) => group.actions.length > 0);
}
